/**
 * 认证 Store
 *
 * 管理 JWT Token 与当前登录用户信息，提供：
 *  - token / user 响应式状态
 *  - isAuthenticated 计算属性
 *  - hasPermission 权限检查方法
 *  - login 登录方法（调用后端 /auth/login）
 *  - logout 登出方法（清除状态并跳转）
 *  - fetchUserInfo 获取当前用户信息（用于刷新页面后恢复状态）
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { apiClient, getToken, setToken, clearToken } from '@/lib/api-client';
import type { UserInfo, LoginResult } from '@/types';

export const useAuthStore = defineStore('auth', () => {
  // ===== State =====

  /** JWT Token（初始化时从 localStorage 恢复） */
  const token = ref<string | null>(getToken());

  /** 当前登录用户信息 */
  const user = ref<UserInfo | null>(null);

  /** 加载状态标记 */
  const loading = ref(false);

  // ===== Getters =====

  /** 是否已登录（同时具备 Token 与用户信息） */
  const isAuthenticated = computed(() => !!token.value && !!user.value);

  /** 当前用户角色编码列表 */
  const userRoleCodes = computed(() => user.value?.roleCodes ?? []);

  /** 当前用户显示名称 */
  const displayName = computed(() => user.value?.displayName ?? user.value?.username ?? '用户');

  // ===== Actions =====

  /**
   * 登录
   * @param username 用户名
   * @param password 密码
   * @returns 登录是否成功
   */
  async function login(username: string, password: string): Promise<boolean> {
    loading.value = true;
    try {
      const result = await apiClient.post<LoginResult>('/auth/login', { username, password });

      token.value = result.token;
      user.value = result.user;
      setToken(result.token);

      return true;
    } catch (err) {
      resetState();
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * 登出
   * 清除本地状态并跳转到登录页
   */
  function logout(): void {
    resetState();
    window.location.href = '/login';
  }

  /**
   * 获取当前用户信息
   * 用于页面刷新后从 Token 恢复用户状态
   */
  async function fetchUserInfo(): Promise<void> {
    if (!token.value) return;

    try {
      const result = await apiClient.get<UserInfo>('/auth/me');
      user.value = result;
    } catch (err) {
      resetState();
      throw err;
    }
  }

  /**
   * 权限检查
   * admin 角色拥有全部权限
   * @param permission 权限标识，如 'inspection-plans:list'
   * @returns 是否拥有该权限
   */
  function hasPermission(permission: string): boolean {
    if (!user.value) return false;
    if (user.value.roleCodes.includes('admin')) return true;
    return user.value.permissions.includes(permission);
  }

  /**
   * 检查是否拥有任意一个指定权限
   */
  function hasAnyPermission(permissions: string[]): boolean {
    return permissions.some((perm) => hasPermission(perm));
  }

  /**
   * 检查是否拥有全部指定权限
   */
  function hasAllPermissions(permissions: string[]): boolean {
    return permissions.every((perm) => hasPermission(perm));
  }

  /** 重置状态（内部方法） */
  function resetState(): void {
    token.value = null;
    user.value = null;
    clearToken();
  }

  return {
    // state
    token,
    user,
    loading,
    // getters
    isAuthenticated,
    userRoleCodes,
    displayName,
    // actions
    login,
    logout,
    fetchUserInfo,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  };
});
